/**
 * @file axios.ts - Cliente HTTP Configurado para API Laravel
 *
 * Este módulo configura o Axios com:
 * - Interceptors de request (JWT + X-Tenant-ID)
 * - Interceptors de response (tratamento de erros)
 * - Redirecionamento automático em 401 (token expirado)
 * - Timeout de 30s
 *
 * Uso:
 * import { api } from '@/lib/axios';
 * const { data } = await api.get('/v1/reservas');
 *
 * @see frontend/src/services/*.service.ts
 */

import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import { getQueryClient } from '@/lib/query-client';
import { localizeErrorPayload } from '@/lib/messages';

/**
 * Limpa por completo a sessão de autenticação no cliente.
 *
 * Motivo: o interceptador de 401 antes limpava apenas os cookies, mas o
 * localStorage ainda mantinha `isAuthenticated: true` na chave `auth-storage`.
 * Isso criava um ciclo infinito depois que o token expirava:
 *   1. o cookie expira e /visao-geral recebe 401;
 *   2. o cliente remove os cookies e navega para /entrar;
 *   3. o estado persistido considera a sessão ativa e volta para /visao-geral.
 *
 * Alteramos o localStorage diretamente, sem importar o
 * estado, para evitar a dependência circular `axios → store → axios`. A chave persistida
 * é `auth-storage` e deve permanecer sincronizada com auth.store.ts.
 */
function clearAuthSession(): void {
  Cookies.remove('auth_token', { path: '/' });
  Cookies.remove('tenant_id', { path: '/' });
  Cookies.remove('property_id', { path: '/' });
  getQueryClient().clear();
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem('auth-storage');
    } catch {
      // localStorage may be unavailable (private mode, SSR) — ignore.
    }
  }
}

/** Flag de controle para evitar múltiplas tentativas de refresh em paralelo */
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value: unknown) => void; reject: (reason?: unknown) => void }> = [];

const AUTH_COOKIE_OPTIONS = {
  expires: 1,
  path: '/',
  sameSite: 'strict' as const,
  secure: typeof window !== 'undefined' ? window.location.protocol === 'https:' : true,
};

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/** URL da API (Laravel) — usa Vite env ou default para dev local */
const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8001/api';

/** URL de fallback — não usada, mantida por compatibilidade */
const API_URL_FALLBACK: string | undefined = undefined;
export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 30000,
});

// Criar instância de fallback
export const apiFallback =
  API_URL_FALLBACK &&
  axios.create({
    baseURL: API_URL_FALLBACK,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    timeout: 30000,
  });

// Request Interceptor - Injetar token e tenant
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = Cookies.get('auth_token');
    const tenantId = Cookies.get('tenant_id');
    const propertyId = Cookies.get('property_id');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
    }

    if (propertyId) {
      config.headers['X-Property-ID'] = propertyId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor - Tratamento de erros com refresh automático
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    localizeErrorPayload(error.response?.data);
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    const isPublicGuestFlow = String(originalRequest.url ?? '').includes('/publico/');

    // O portal do hóspede usa uma sessão HttpOnly própria. Um 401 nesse fluxo
    // significa que o hóspede deve informar o código temporário, não que a SPA
    // administrativa precise redirecioná-lo para o login de funcionários.
    if (status === 401 && isPublicGuestFlow) {
      return Promise.reject(error);
    }

    if (status === 428) {
      if (
        typeof window !== 'undefined' &&
        !window.location.pathname.includes('/alterar-senha')
      ) {
        window.location.href = '/alterar-senha';
      }
      return Promise.reject(error);
    }

    // Token expirado — tentar refresh antes de deslogar
    if (status === 401 && !originalRequest._retry) {
      const currentToken = Cookies.get('auth_token');

      // Se não há token, não há o que renovar — ir para login
      if (!currentToken) {
        clearAuthSession();
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/entrar')) {
          window.location.href = '/entrar';
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Se já está renovando, enfileirar a requisição original
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (originalRequest.headers) {
            (originalRequest.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
          }
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await api.post<{ data: { access_token: string } }>('/v1/autenticacao/renovar');
        const newToken = data?.data?.access_token;

        if (newToken) {
          Cookies.set('auth_token', newToken, AUTH_COOKIE_OPTIONS);
          api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          processQueue(null, newToken);
          if (originalRequest.headers) {
            (originalRequest.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
          }
          return api(originalRequest);
        }

        // Refresh succeeded HTTP-wise but no token in payload — treat as failure
        // para não deixar silenciosamente o usuário em uma sessão parcialmente autenticada.
        processQueue(error, null);
        clearAuthSession();
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/entrar')) {
          window.location.href = '/entrar';
        }
        return Promise.reject(error);
      } catch {
        processQueue(error, null);
        clearAuthSession();
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/entrar')) {
          window.location.href = '/entrar';
        }
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    // Assinatura vencida — redirecionar para tela de bloqueio
    if (status === 403) {
      const responseData = error.response?.data as { error?: string } | undefined;
      if (
        responseData?.error === 'PAYMENT_REQUIRED' &&
        typeof window !== 'undefined' &&
        !window.location.pathname.includes('/billing/blocked')
      ) {
        window.location.href = '/billing/blocked';
        return Promise.reject(error);
      }
      console.error('[API] Acesso negado:', status);
    }

    // Validation errors
    if (status === 422) {
      console.error('[API] Erro de validação:', status);
    }

    // Server error
    if (status && status >= 500) {
      console.error('[API] Erro do servidor:', status);
    }

    return Promise.reject(error);
  }
);

export default api;
